import { useMemo } from "react";
import {
  DocumentImport as ImportPanel,
  type DocumentImportServices,
} from "@proof/authoring";
import type { Project, ImportReport, ImportResult } from "@proof/core";
import { Modal } from "./components";
import { useWorkspaceTransport } from "./WorkspaceContext";
export function DocumentImport({
  onClose,
  onImport,
  title = "Import document",
  jsonTenant,
  onArchiveImport,
  onArchiveOpen,
}: {
  onClose: () => void;
  onImport: (project: Project, report?: ImportReport) => Promise<void>;
  title?: string;
  jsonTenant?: string;
  onArchiveImport?: (
    project: Project,
    report: ImportReport,
    signal: AbortSignal,
  ) => Promise<void>;
  onArchiveOpen?: () => void;
}) {
  const transport = useWorkspaceTransport(),
    { request } = transport;
  const trustedJsonTenant =
    jsonTenant || (transport.demo ? "demo" : transport.principal?.tenantId);
  const services = useMemo<DocumentImportServices>(
    () => ({
      archive: {
        startUpload: async (input, signal) => {
          const response = await request("/project-archives", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
            signal,
          });
          const result = await response.json();
          if (!response.ok)
            throw new Error(result.error || "Archive staging failed");
          return result;
        },
        uploadStatus: async (id, signal) => {
          const response = await request(
            `/project-archives/${encodeURIComponent(id)}`,
            { signal },
          );
          const result = await response.json();
          if (!response.ok)
            throw new Error(result.error || "Archive status failed");
          return result;
        },
        uploadChunk: async (id, offset, chunk, signal) => {
          const response = await request(
            `/project-archives/${encodeURIComponent(id)}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/octet-stream",
                "X-Upload-Offset": String(offset),
              },
              body: chunk,
              signal,
            },
          );
          const result = await response.json();
          if (!response.ok)
            throw new Error(result.error || "Archive upload failed");
          return result;
        },
        importProject: async (id, entryId, template, signal) => {
          const response = await request(
            `/project-archives/${encodeURIComponent(id)}/projects`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ entryId, template }),
              signal,
            },
          );
          const result = await response.json();
          if (!response.ok)
            throw new Error(result.error || "Project import failed");
          return result;
        },
        removeUpload: async (id, signal) => {
          const response = await request(
            `/project-archives/${encodeURIComponent(id)}`,
            { method: "DELETE", signal },
          );
          if (!response.ok) throw new Error("Archive cleanup failed");
          return response.json();
        },
      },
      prepareProject: async (project, signal) => {
        const response = await request("/documents/image-urls", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ project }),
          signal,
        });
        const result = await response.json();
        if (!response.ok)
          throw new Error(result.error || "Image URL import failed");
        return result as Project;
      },
      importDocument: async (file, selection, signal) => {
        const response = await request(
          "/documents/import" +
            (selection.packageContent === "edited-idml"
              ? "?packageContent=edited-idml"
              : ""),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/octet-stream",
              "X-Filename": encodeURIComponent(file.name),
            },
            body: file,
            signal,
          },
        );
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Import failed");
        return result as ImportResult;
      },
    }),
    [request],
  );
  return (
    <Modal title={title} onClose={onClose} wide>
      <ImportPanel
        services={services}
        onImport={onImport}
        title={title}
        jsonTenant={trustedJsonTenant}
        onOpen={onClose}
        onArchiveImport={onArchiveImport}
        onArchiveOpen={onArchiveOpen}
      />
    </Modal>
  );
}
